
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigRequest} from './ConfigRequest';
import {FieldModelConfigRequest} from './ConfigRequest';
import {LanguageGetResponse} from './LanguageGetResponse';

/**
 * LanguageGetRequest struct
 */
class LanguageGetRequest extends ConfigRequest {
  /**
   * Initialize struct
   * @param {!ConfigRequest=} parent
   * @param {UUID=} languageId
   * @param {string=} languageName
   * @constructor
   */
  constructor (parent = new ConfigRequest(), languageId = null, languageName = null) {
    super()
    super.copy(parent)
    this.LanguageId = languageId
    this.LanguageName = languageName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LanguageGetRequest}
   * @param {!LanguageGetRequest} other Other struct
   * @returns {!LanguageGetRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.LanguageId != null) {
      this.LanguageId = new UUID(other.LanguageId)
    } else {
      this.LanguageId = null
    }
    if (other.LanguageName != null) {
      this.LanguageName = other.LanguageName
    } else {
      this.LanguageName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LanguageGetRequest}
   * @returns {!LanguageGetRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LanguageGetRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LanguageGetRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LanguageGetRequest}
   * @param {!LanguageGetRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LanguageGetRequest)) {
      throw new TypeError('Instance of LanguageGetRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LanguageGetRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      LanguageId: ((this.LanguageId != null) ? this.LanguageId.toString() : null),
      LanguageName: ((this.LanguageName != null) ? this.LanguageName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LanguageGetRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LanguageGetRequest} other Object value
   * @returns {!LanguageGetRequest} Created struct
   */
  static fromObject (other) {
    return new LanguageGetRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LanguageGetRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LanguageGetRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LanguageGetRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6567
  }
}

export { LanguageGetRequest };

/**
 * Fast Binary Encoding LanguageGetRequest field model
 */
class FieldModelLanguageGetRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigRequest(buffer, 4 + 4)
    this.LanguageId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.LanguageName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.LanguageId.fbeOffset + this.LanguageId.fbeSize), buffer, this.LanguageId.fbeOffset + this.LanguageId.fbeSize)
  }

  /**
   * Get the ConfigRequest field model
   * @this {!FieldModelLanguageGetRequest}
   * @returns {!FieldModelConfigRequest} ConfigRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelLanguageGetRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLanguageGetRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.LanguageId.fbeSize + this.LanguageName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLanguageGetRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.LanguageId.fbeExtra + this.LanguageName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLanguageGetRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLanguageGetRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLanguageGetRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6567
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLanguageGetRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLanguageGetRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.LanguageId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LanguageId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LanguageId.fbeSize

    if ((fbeCurrentSize + this.LanguageName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LanguageName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LanguageName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLanguageGetRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLanguageGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLanguageGetRequest}
   * @param {!LanguageGetRequest} fbeValue Default value, defaults is new LanguageGetRequest()
   * @returns {!LanguageGetRequest} LanguageGetRequest value
   */
  get (fbeValue = new LanguageGetRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLanguageGetRequest}
   * @param {!LanguageGetRequest} fbeValue LanguageGetRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.LanguageId.fbeSize) <= fbeStructSize) {
      fbeValue.LanguageId = this.LanguageId.get()
    } else {
      fbeValue.LanguageId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LanguageId.fbeSize

    if ((fbeCurrentSize + this.LanguageName.fbeSize) <= fbeStructSize) {
      fbeValue.LanguageName = this.LanguageName.get()
    } else {
      fbeValue.LanguageName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LanguageName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLanguageGetRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLanguageGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLanguageGetRequest}
   * @param {!LanguageGetRequest} fbeValue LanguageGetRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLanguageGetRequest}
   * @param {LanguageGetRequest} fbeValue LanguageGetRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.LanguageId.set(fbeValue.LanguageId)
    this.LanguageName.set(fbeValue.LanguageName)
  }
}

export { FieldModelLanguageGetRequest };

/**
 * Fast Binary Encoding LanguageGetRequest model
 */
class LanguageGetRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLanguageGetRequest(this.buffer, 4)
  }

  /**
   * Get the LanguageGetRequest model
   * @this {!LanguageGetRequestModel}
   * @returns {!FieldModelLanguageGetRequest} model LanguageGetRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LanguageGetRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LanguageGetRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LanguageGetRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LanguageGetRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLanguageGetRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LanguageGetRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LanguageGetRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LanguageGetRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LanguageGetRequestModel}
   * @param {!LanguageGetRequest} value LanguageGetRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LanguageGetRequestModel}
   * @param {!LanguageGetRequest} value LanguageGetRequest value, defaults is new LanguageGetRequest()
   * @return {!object} Deserialized LanguageGetRequest value and its size
   */
  deserialize (value = new LanguageGetRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LanguageGetRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LanguageGetRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LanguageGetRequestModel}
   * @param {!number} prev Previous LanguageGetRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LanguageGetRequestModel };
LanguageGetRequest.__has_response = true;
LanguageGetRequest.__response_class = LanguageGetResponse;
